package er.prateek.crossly.generation.math

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.math.MathCell
import er.prateek.crossly.data.model.math.MathCellKind
import er.prateek.crossly.data.model.math.MathEquation
import er.prateek.crossly.data.model.math.MathOp
import er.prateek.crossly.data.model.math.MathPuzzle
import er.prateek.crossly.data.model.math.isUniquelyDeducible
import er.prateek.crossly.data.model.math.isWellFormed
import er.prateek.crossly.game.crossword.PuzzleRandom
import er.prateek.crossly.game.crossword.stableSeed

/**
 * Generates math crosswords at runtime: deterministic for a given seed (so a resumed attempt is
 * byte-identical) but fresh each new attempt via the caller's nonce.
 *
 * Difficulty ramps **within** a tier, not just between tiers: as a level's index climbs through its
 * tier, the grid gains equations and changes shape ([MathGridTemplates.select] rotates shapes by
 * index), the number range and blank ratio grow, and each tier's harder operators unlock partway up.
 * So Easy level 1 is a tiny two-term addition cross and Easy level 30 is a three-equation grid with
 * subtraction and more blanks -- and no two consecutive levels look the same.
 *
 * Because templates are acyclic (see [MathGridTemplates]), the grid can always be filled in one pass,
 * and blanks are removed only while the puzzle stays uniquely deducible -- so there is never guessing.
 */
object MathCrosswordGenerator {

    /** Remote-config-driven overrides applied on top of the per-level difficulty. */
    data class Options(
        /** `0` keeps the ramped default; otherwise the fraction of number cells left blank. */
        val blankRatioOverride: Double = 0.0,
        val allowMultiply: Boolean = true,
        val allowDivide: Boolean = true,
    )

    private enum class Role { A, B, C }

    private data class EqAssignment(val a: Int, val b: Int, val c: Int, val op: MathOp)

    /** The resolved difficulty for one specific level. */
    private data class Rung(
        val equations: Int,
        val ops: List<MathOp>,
        val maxOperand: Int,
        val maxMulOperand: Int,
        val maxDigits: Int,
        val blankRatio: Double,
    )

    private const val FILL_ATTEMPTS = 240
    private const val GEN_ATTEMPTS = 12
    private const val MAX_SEED_ATTEMPTS = 24

    private fun lerpInt(from: Int, to: Int, t: Double): Int = (from + (to - from) * t).toInt()
    private fun lerp(from: Double, to: Double, t: Double): Double = from + (to - from) * t

    /**
     * How many equations a level has, climbing from [min] to [max] in equal stages across the tier.
     * This is the most *felt* lever -- more equations means a bigger grid with more cells to fill --
     * so it is stepped (not smoothly ramped) to give a clear bump every few levels.
     */
    private fun equationSteps(min: Int, max: Int, p: Double): Int =
        (min + ((max - min + 1) * p).toInt()).coerceIn(min, max)

    /** The level's difficulty, as a function of its tier and its position within the tier. */
    private fun rungFor(tier: DifficultyTier, index: Int, levelsPerTier: Int, options: Options): Rung {
        val span = (levelsPerTier - 1).coerceAtLeast(1)
        val p = (index - 1).coerceIn(0, span).toDouble() / span // 0.0 at the first level, 1.0 at the last
        // × and ÷ phase in as a tier is climbed (earlier in the harder tiers), and the numbers grow
        // from single digits to 2- and 3-digit values (10-99 and beyond) in Medium and above.
        fun opsFor(mulFrom: Double, divFrom: Double) = buildList {
            add(MathOp.ADD); add(MathOp.SUB)
            if (p >= mulFrom) add(MathOp.MUL)
            if (p >= divFrom) add(MathOp.DIV)
        }
        val base = when (tier) {
            DifficultyTier.EASY -> Rung(
                equations = equationSteps(2, 4, p),
                ops = if (p < 0.08) listOf(MathOp.ADD) else opsFor(mulFrom = 0.5, divFrom = 0.8),
                maxOperand = lerpInt(3, 20, p),
                maxMulOperand = lerpInt(4, 9, p),
                maxDigits = 2,
                blankRatio = lerp(0.40, 0.72, p),
            )
            DifficultyTier.MEDIUM -> Rung(
                equations = equationSteps(2, 4, p),
                ops = opsFor(mulFrom = 0.1, divFrom = 0.35),
                maxOperand = lerpInt(15, 99, p),
                maxMulOperand = lerpInt(6, 15, p),
                maxDigits = 3,
                blankRatio = lerp(0.42, 0.72, p),
            )
            DifficultyTier.HARD -> Rung(
                equations = equationSteps(3, 4, p),
                ops = opsFor(mulFrom = 0.0, divFrom = 0.2),
                maxOperand = lerpInt(40, 99, p),
                maxMulOperand = lerpInt(9, 30, p),
                maxDigits = 3,
                blankRatio = lerp(0.45, 0.70, p),
            )
            DifficultyTier.EXPERT -> Rung(
                equations = equationSteps(3, 4, p),
                ops = opsFor(mulFrom = 0.0, divFrom = 0.0),
                maxOperand = lerpInt(60, 99, p),
                maxMulOperand = lerpInt(12, 40, p),
                maxDigits = 3,
                blankRatio = lerp(0.48, 0.68, p),
            )
        }
        // + and - are never filtered out, so there is always at least one usable operator.
        val ops = base.ops.filter { (it != MathOp.MUL || options.allowMultiply) && (it != MathOp.DIV || options.allowDivide) }
            .ifEmpty { listOf(MathOp.ADD, MathOp.SUB) }
        val blank = if (options.blankRatioOverride <= 0.0) base.blankRatio else options.blankRatioOverride.coerceIn(0.2, 0.8)
        return base.copy(ops = ops, blankRatio = blank)
    }

    /**
     * The level's content for a given attempt. [levelId] (stable) becomes the puzzle id used for
     * scoring; [index] (1-based) sets both the shape and the difficulty within the tier; [nonce]
     * varies the numbers per play. [levelsPerTier] is how many levels the tier spans, which sets how
     * quickly difficulty ramps. Always returns a well-formed puzzle (falls back to a simple additive
     * shape if a hard rung's seed is unlucky).
     */
    fun generateForLevel(
        levelId: String,
        tier: DifficultyTier,
        index: Int,
        nonce: Long,
        options: Options = Options(),
        levelsPerTier: Int = 30,
    ): MathPuzzle {
        val rung = rungFor(tier, index, levelsPerTier, options)
        val template = MathGridTemplates.select(rung.equations, index)
        val base = levelId.stableSeed() + nonce
        for (s in 0 until MAX_SEED_ATTEMPTS) {
            generate(template, rung, tier, base + s * 7919L, levelId)?.let { return it }
        }
        // Last-resort: a 2-equation additive shape always fills.
        val easyRung = Rung(2, listOf(MathOp.ADD, MathOp.SUB), maxOperand = 9, maxMulOperand = 9, maxDigits = 2, blankRatio = 0.4)
        for (s in 0 until MAX_SEED_ATTEMPTS) {
            generate(MathGridTemplates.select(2, s + 1), easyRung, tier, base + s, levelId)?.let { return it }
        }
        error("Math generator exhausted every seed for $levelId")
    }

    /** One generation attempt at a resolved difficulty. Returns null if this seed couldn't be completed. */
    private fun generate(template: MathTemplate, rung: Rung, tier: DifficultyTier, seed: Long, id: String): MathPuzzle? {
        repeat(GEN_ATTEMPTS) { attempt ->
            val rng = PuzzleRandom(seed + attempt)
            val filled = fillGrid(template, rung, rng) ?: return@repeat
            val puzzle = blank(filled, template, id, tier, rung, rng)
            if (puzzle.isWellFormed()) return puzzle
        }
        return null
    }

    private fun fillGrid(template: MathTemplate, rung: Rung, rng: PuzzleRandom): List<MathCell>? {
        val cells = template.blankCells()
        val equations = template.equationList()
        val values = HashMap<Int, Int>()
        val remaining = equations.toMutableList()
        while (remaining.isNotEmpty()) {
            // An acyclic template always leaves an equation with <=1 fixed number to process next.
            val eq = remaining.firstOrNull { e -> e.numberIndices.count { it in values } <= 1 } ?: return null
            val knownIndex = eq.numberIndices.firstOrNull { it in values }
            val known = knownIndex?.let { roleOf(eq, it) to values.getValue(it) }
            val asg = fillEquation(rng, rung, known) ?: return null
            values[eq.operandAIndex] = asg.a
            values[eq.operandBIndex] = asg.b
            values[eq.resultIndex] = asg.c
            cells[eq.operatorIndex] = cells[eq.operatorIndex].copy(text = asg.op.glyph)
            remaining.remove(eq)
        }
        for (eq in equations) cells[eq.cells[3]] = cells[eq.cells[3]].copy(text = "=")
        for ((idx, v) in values) {
            cells[idx] = cells[idx].copy(solution = v.toString(), text = v.toString(), given = true)
        }
        return cells
    }

    /** Greedily blanks number cells while the puzzle stays uniquely deducible. */
    private fun blank(
        filled: List<MathCell>,
        template: MathTemplate,
        id: String,
        tier: DifficultyTier,
        rung: Rung,
        rng: PuzzleRandom,
    ): MathPuzzle {
        val cells = filled.toMutableList()
        val equations = template.equationList()
        val numberIndices = cells.indices.filter { cells[it].kind == MathCellKind.NUMBER }
        val target = (numberIndices.size * rung.blankRatio).toInt().coerceAtLeast(1)
        var blanked = 0
        for (idx in rng.shuffled(numberIndices)) {
            if (blanked >= target) break
            val prev = cells[idx]
            cells[idx] = prev.copy(given = false, text = "")
            val candidate = MathPuzzle(id, tier, template.width, template.height, cells.toList(), equations)
            if (candidate.isUniquelyDeducible()) blanked++ else cells[idx] = prev
        }
        return MathPuzzle(id, tier, template.width, template.height, cells.toList(), equations)
    }

    private fun roleOf(eq: MathEquation, index: Int): Role = when (index) {
        eq.operandAIndex -> Role.A
        eq.operandBIndex -> Role.B
        else -> Role.C
    }

    private fun fillEquation(rng: PuzzleRandom, rung: Rung, known: Pair<Role, Int>?): EqAssignment? {
        repeat(FILL_ATTEMPTS) {
            val op = rung.ops[rng.nextInt(rung.ops.size)]
            val asg = when (op) {
                MathOp.ADD -> genAdd(rng, rung, known)
                MathOp.SUB -> genSub(rng, rung, known)
                MathOp.MUL -> genMul(rng, rung, known)
                MathOp.DIV -> genDiv(rng, rung, known)
            }
            if (asg != null && asg.withinDigits(rung.maxDigits)) return asg
        }
        return null
    }

    private fun EqAssignment.withinDigits(maxDigits: Int): Boolean {
        val limit = pow10(maxDigits)
        return a in 0 until limit && b in 0 until limit && c in 0 until limit
    }

    private fun pow10(n: Int): Int {
        var r = 1
        repeat(n) { r *= 10 }
        return r
    }

    private fun PuzzleRandom.intIn(lo: Int, hi: Int): Int =
        if (hi <= lo) lo else lo + nextInt(hi - lo + 1)

    // a + b = c
    private fun genAdd(rng: PuzzleRandom, rung: Rung, known: Pair<Role, Int>?): EqAssignment? {
        val (role, v) = known ?: (null to 0)
        return when (role) {
            null -> { val a = rng.intIn(1, rung.maxOperand); val b = rng.intIn(1, rung.maxOperand); EqAssignment(a, b, a + b, MathOp.ADD) }
            Role.A -> { val b = rng.intIn(1, rung.maxOperand); EqAssignment(v, b, v + b, MathOp.ADD) }
            Role.B -> { val a = rng.intIn(1, rung.maxOperand); EqAssignment(a, v, a + v, MathOp.ADD) }
            Role.C -> { if (v < 2) null else { val a = rng.intIn(1, v - 1); EqAssignment(a, v - a, v, MathOp.ADD) } }
        }
    }

    // a - b = c, a >= b >= 0
    private fun genSub(rng: PuzzleRandom, rung: Rung, known: Pair<Role, Int>?): EqAssignment? {
        val (role, v) = known ?: (null to 0)
        return when (role) {
            null -> { val a = rng.intIn(2, rung.maxOperand); val b = rng.intIn(1, a); EqAssignment(a, b, a - b, MathOp.SUB) }
            Role.A -> { if (v < 1) null else { val b = rng.intIn(1, v); EqAssignment(v, b, v - b, MathOp.SUB) } }
            Role.B -> { val a = rng.intIn(v, rung.maxOperand + v); EqAssignment(a, v, a - v, MathOp.SUB) }
            Role.C -> { val b = rng.intIn(1, rung.maxOperand); EqAssignment(b + v, b, v, MathOp.SUB) }
        }
    }

    // a * b = c
    private fun genMul(rng: PuzzleRandom, rung: Rung, known: Pair<Role, Int>?): EqAssignment? {
        val (role, v) = known ?: (null to 0)
        return when (role) {
            null -> { val a = rng.intIn(2, rung.maxMulOperand); val b = rng.intIn(2, rung.maxMulOperand); EqAssignment(a, b, a * b, MathOp.MUL) }
            Role.A -> { val b = rng.intIn(2, rung.maxMulOperand); EqAssignment(v, b, v * b, MathOp.MUL) }
            Role.B -> { val a = rng.intIn(2, rung.maxMulOperand); EqAssignment(a, v, a * v, MathOp.MUL) }
            Role.C -> { val a = randomDivisor(rng, v, 2, rung.maxMulOperand) ?: return null; EqAssignment(a, v / a, v, MathOp.MUL) }
        }
    }

    // a / b = c  (a = b * c, exact)
    private fun genDiv(rng: PuzzleRandom, rung: Rung, known: Pair<Role, Int>?): EqAssignment? {
        val (role, v) = known ?: (null to 0)
        return when (role) {
            null -> { val b = rng.intIn(2, rung.maxMulOperand); val c = rng.intIn(2, rung.maxMulOperand); EqAssignment(b * c, b, c, MathOp.DIV) }
            Role.A -> { val b = randomDivisor(rng, v, 2, rung.maxMulOperand) ?: return null; EqAssignment(v, b, v / b, MathOp.DIV) }
            Role.B -> { if (v < 2) null else { val c = rng.intIn(1, rung.maxMulOperand); EqAssignment(v * c, v, c, MathOp.DIV) } }
            Role.C -> { if (v < 1) null else { val b = rng.intIn(2, rung.maxMulOperand); EqAssignment(b * v, b, v, MathOp.DIV) } }
        }
    }

    /** A random divisor of [v] in `[lo..hi]`, or null if none. */
    private fun randomDivisor(rng: PuzzleRandom, v: Int, lo: Int, hi: Int): Int? {
        if (v <= 0) return null
        val divisors = (lo..hi).filter { v % it == 0 }
        return rng.shuffled(divisors).firstOrNull()
    }
}
